import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  FormControlLabel,
  Checkbox,
  Button,
  Accordion,
  AccordionSummary,
  FormHelperText,
  FormGroup,
  TextField,
} from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { daysList, FormInputs } from "../constants";
import CustomDatePicker from "src/shared/components/custom-date-picker";
import CustomTimePicker from "src/shared/components/custom-time-picker";

interface IProps {
  locIndex: number;
  operatorType: string;
}

const LocationSchedule: React.FC<IProps> = ({ locIndex, operatorType }) => {
  const [expanded, setExpanded] = useState<string | false>(
    true ? "panel1" : false,
  );

  const haveFlexibleHoursRoles = [
    "Zone Manager",
    "Location Manager",
    "Supervisor",
  ];

  const haveFlexibleHours = haveFlexibleHoursRoles.includes(operatorType);

  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<FormInputs>();

  const {
    fields: shiftFields,
    append: appendShift,
    remove: removeShift,
  } = useFieldArray({
    control,
    name: `locations.${locIndex}.shifts`,
  });

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleAppendAnotherShift = () => {
    appendShift({
      shiftType: "",
      startTime: null,
      endTime: null,
      days: [],
      startDate: null,
      endDate: null,
    });
  };

  const handleDeleteShift = (index: number) => {
    removeShift(index);
  };

  return (
    <Accordion
      expanded={expanded === "panel1"}
      onChange={handleChange("panel1")}
      variant="outlined"
      sx={{
        borderRadius: "10px",
        px: { xs: 1.5, md: 3 },
        pt: { xs: 1, md: 1.5 },
        pb: { xs: 1.5, md: 3 },
        mt: 3,
        backgroundColor: "transparent",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1bh-content"
        id="panel1bh-header"
        sx={{ p: "0" }}
      >
        <Typography component="span">
          {watch("locations")?.[locIndex]?.locationName}
        </Typography>
      </AccordionSummary>
      {shiftFields.map((shift, shiftIndex) => (
        <Box key={shift.id} pb={3} borderBottom={"1px solid #5B5B5B"} mb={3}>
          <FormControl
            fullWidth
            size="small"
            error={
              !!errors.locations?.[locIndex]?.shifts?.[shiftIndex]?.shiftType
            }
            sx={{ width: { xs: "100%", sm: "300px" }, mb: 2 }}
          >
            <InputLabel id="demo-simple-select-label">
              Select a shift
            </InputLabel>
            <Controller
              name={`locations.${locIndex}.shifts.${shiftIndex}.shiftType`}
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  label={"Select a shift"}
                >
                  <MenuItem value={"morning"}>Morning Shift</MenuItem>
                  <MenuItem value={"day"}>Day Shift</MenuItem>
                  <MenuItem value={"night"}>Night Shift</MenuItem>
                </Select>
              )}
            />
            <FormHelperText id="my-helper-text">
              {
                errors.locations?.[locIndex]?.shifts?.[shiftIndex]?.shiftType
                  ?.message
              }
            </FormHelperText>
          </FormControl>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            gap={2}
          >
            <Typography fontSize={"14px"}>Shift Hours:</Typography>
            <Stack direction={"row"} gap={1} alignItems={"center"}>
              <Typography fontSize={"14px"}>From</Typography>
              <Controller
                name={`locations.${locIndex}.shifts.${shiftIndex}.startTime`}
                control={control}
                render={({ field }) => (
                  <CustomTimePicker
                    {...field}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: { width: "135px" },
                        error:
                          !!errors.locations?.[locIndex]?.shifts?.[shiftIndex]
                            ?.startTime,
                        helperText:
                          errors.locations?.[locIndex]?.shifts?.[shiftIndex]
                            ?.startTime?.message ?? "",
                      },
                    }}
                  />
                )}
              />
            </Stack>
            <Stack direction={"row"} gap={1} alignItems={"center"}>
              <Typography my={1.5} fontSize={"14px"}>
                To
              </Typography>
              <Controller
                name={`locations.${locIndex}.shifts.${shiftIndex}.endTime`}
                control={control}
                render={({ field }) => (
                  <CustomTimePicker
                    {...field}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: { width: "135px" },
                        error:
                          !!errors.locations?.[locIndex]?.shifts?.[shiftIndex]
                            ?.endTime,
                        helperText:
                          errors.locations?.[locIndex]?.shifts?.[shiftIndex]
                            ?.endTime?.message ?? "",
                      },
                    }}
                  />
                )}
              />
            </Stack>
            {/* flexible hours */}
            {haveFlexibleHours ? (
              <Stack direction={"row"} gap={1} alignItems={"center"} ml={2}>
                <Typography my={1.5} fontSize={"14px"}>
                  Flexible Hours
                </Typography>
                <Controller
                  name={`locations.${locIndex}.shifts.${shiftIndex}.flexibleHours`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type={"number"}
                      sx={{ width: "135px" }}
                      label=""
                      {...field}
                      error={
                        !!errors.locations?.[locIndex]?.shifts?.[shiftIndex]
                          ?.flexibleHours
                      }
                      helperText={
                        errors.locations?.[locIndex]?.shifts?.[shiftIndex]
                          ?.flexibleHours?.message ?? ""
                      }
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                    />
                  )}
                />
              </Stack>
            ) : null}
          </Stack>

          <Stack mt={1} direction={"row"} alignItems={"center"} gap={3}>
            <Typography>Repeat</Typography>
            <Controller
              name={`locations.${locIndex}.shifts.${shiftIndex}.days`}
              control={control}
              render={({ field }) => {
                const handleChange = (event: any) => {
                  const { value } = event.target;
                  const newValue = field.value?.includes(value)
                    ? field.value.filter((v) => v !== value) // remove if already selected
                    : [...(field.value || []), value]; // add if not selected
                  field.onChange(newValue);
                };

                const error =
                  errors?.locations?.[locIndex]?.shifts?.[shiftIndex]?.days
                    ?.message;

                return (
                  <FormControl
                    component="fieldset"
                    error={!!error}
                    variant="standard"
                  >
                    <FormGroup row>
                      {daysList.map((day) => (
                        <FormControlLabel
                          key={day.value}
                          control={
                            <Checkbox
                              value={day.value}
                              checked={
                                field.value?.includes(day.value) || false
                              }
                              onChange={handleChange}
                              size="small"
                            />
                          }
                          label={day.label}
                        />
                      ))}
                    </FormGroup>
                    <FormHelperText id="my-helper-text">{error}</FormHelperText>
                  </FormControl>
                );
              }}
            />
          </Stack>
          <Stack
            mt={1}
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            gap={2}
          >
            <Typography>Shift Duration</Typography>
            <Stack direction={"row"} gap={1} alignItems={"center"}>
              <Typography fontSize={"14px"}>From</Typography>
              <Controller
                name={`locations.${locIndex}.shifts.${shiftIndex}.startDate`}
                control={control}
                render={({ field }) => (
                  <CustomDatePicker
                    {...field}
                    format={"dd/MM/yyyy"}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: { width: "145px", mr: 2 },
                        error:
                          !!errors.locations?.[locIndex]?.shifts?.[shiftIndex]
                            ?.startDate,
                        helperText:
                          errors.locations?.[locIndex]?.shifts?.[shiftIndex]
                            ?.startDate?.message ?? "",
                        inputProps: { readOnly: true },
                      },
                    }}
                    minDate={new Date()}
                  />
                )}
              />
            </Stack>
            <Stack direction={"row"} alignItems={"center"} gap={3}>
              <Stack direction={"row"} gap={1} alignItems={"center"}>
                <Typography fontSize={"14px"}>To</Typography>
                <Controller
                  name={`locations.${locIndex}.shifts.${shiftIndex}.endDate`}
                  control={control}
                  render={({ field }) => (
                    <CustomDatePicker
                      {...field}
                      format={"dd/MM/yyyy"}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          sx: { width: "145px", mr: 2 },
                          error:
                            !!errors.locations?.[locIndex]?.shifts?.[shiftIndex]
                              ?.endDate,
                          helperText:
                            errors.locations?.[locIndex]?.shifts?.[shiftIndex]
                              ?.endDate?.message ?? "",
                          inputProps: { readOnly: true },
                        },
                      }}
                      minDate={new Date()}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </Stack>

          {shiftFields.length > 1 ? (
            <Stack alignItems={"flex-end"} mt={3}>
              <Button
                variant="outlined"
                size="small"
                sx={{ gap: 0.5 }}
                onClick={() => handleDeleteShift(shiftIndex)}
              >
                {/* <img src={DeleteIcon} alt="" /> */}
                Delete Shift
              </Button>
            </Stack>
          ) : null}
        </Box>
      ))}
      <Stack alignItems={"flex-end"} mt={3}>
        <Button
          variant="contained"
          sx={{ gap: 0.5 }}
          onClick={handleAppendAnotherShift}
        >
          <AddBoxIcon fontSize="small" sx={{ opacity: 0.7 }} />
          Add Another Shift
        </Button>
      </Stack>
    </Accordion>
  );
};

export default LocationSchedule;
